import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../../types';

interface Props {
  message: ChatMessage;
  isMine: boolean;
  isAdmin?: boolean;           // l'observateur courant est-il COMPANY_ADMIN ?
  showSender: boolean;         // afficher le nom (groupes / changement d'expéditeur)
  onEdit?: (messageId: string, currentContent: string) => void;
  onDelete?: (messageId: string) => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Linkifier basique : convertit les http(s)://... en liens cliquables.
// Tout le reste est rendu en texte brut (jamais d'HTML interprété).
function renderContent(content: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /(https?:\/\/[^\s]+)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) parts.push(content.substring(lastIndex, match.index));
    parts.push(
      <a key={`link-${key++}`} href={match[0]} target="_blank" rel="noopener noreferrer"
         className="text-cyan-300 underline hover:text-cyan-200">{match[0]}</a>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) parts.push(content.substring(lastIndex));
  return parts;
}

// Le message est-il encore éditable (auteur + < 15 min depuis l'envoi) ?
function isEditable(createdAt: string): boolean {
  const ts = new Date(createdAt.replace(' ', 'T')).getTime();
  if (!ts) return false;
  return (Date.now() - ts) < 15 * 60 * 1000;
}

const MessageBubble: React.FC<Props> = ({ message, isMine, isAdmin, showSender, onEdit, onDelete }) => {
  const isDeleted = !!message.deleted_at;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Fermer le menu si clic à l'extérieur
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const canEdit = isMine && !isDeleted && isEditable(message.created_at) && !!onEdit;
  const canDelete = !isDeleted && !!onDelete && (isMine || isAdmin);
  const hasMenu = canEdit || canDelete;

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2 group`}>
      <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
        {showSender && !isMine && (
          <span className="text-[10px] font-bold text-[#7f93bc] px-3 mb-0.5">{message.sender_name}</span>
        )}

        <div className="relative flex items-center gap-1">
          {/* Bouton menu (apparait au hover sur ses propres messages) */}
          {hasMenu && isMine && (
            <div ref={menuRef} className="relative opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => setMenuOpen(o => !o)}
                className="p-1 rounded-md hover:bg-white/10 text-[#7f93bc] hover:text-[#e0eaff]"
                aria-label="Actions sur le message"
                title="Actions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute bottom-full right-0 mb-1 min-w-[140px] rounded-lg border border-[rgba(100,140,210,0.3)] bg-[rgba(15,30,60,0.97)] backdrop-blur shadow-2xl z-20 py-1">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onEdit!(message.id, message.content); }}
                      className="block w-full text-left px-3 py-1.5 text-xs text-[#e0eaff] hover:bg-cyan-500/15"
                    >
                      ✏️ Modifier
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onDelete!(message.id); }}
                      className="block w-full text-left px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-500/15"
                    >
                      🗑 Supprimer
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div
            className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
              isMine
                ? 'bg-cyan-600/30 border border-cyan-500/40 text-[#e0eaff] rounded-br-md'
                : 'bg-[rgba(100,140,210,0.12)] border border-[rgba(100,140,210,0.25)] text-[#e0eaff] rounded-bl-md'
            } ${isDeleted ? 'italic opacity-60' : ''}`}
          >
            {isDeleted ? <em>Message supprimé</em> : renderContent(message.content)}
          </div>

          {/* Bouton supprimer côté admin pour les messages reçus */}
          {hasMenu && !isMine && canDelete && (
            <button
              type="button"
              onClick={() => onDelete!(message.id)}
              className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-rose-500/15 text-[#7f93bc] hover:text-rose-300 transition-opacity"
              aria-label="Supprimer (admin)"
              title="Supprimer (action admin)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
              </svg>
            </button>
          )}
        </div>

        <span className="text-[9px] text-[#546a96] px-3 mt-0.5">
          {formatTime(message.created_at)}
          {message.edited_at && !isDeleted && <span className="ml-1">(modifié)</span>}
        </span>
      </div>
    </div>
  );
};

export default MessageBubble;
